import React, { useCallback, useEffect, useState } from 'react';
import { PostDataService, isPastRestoreWindow } from '../services/postDataService';
import { DeletedUpload } from '../models/post';

// ---------------------------------------------------------------------------
// Deleted images on a post, and the way back.
//
// Sits with the post's other photographs, because that is where someone is
// standing when they realise they removed the wrong one. It stays out of the
// way until there is something to show: no deleted images, no control.
//
// Thumbnails here are the DISPLAYED variant — the blurred copy wherever a blur
// exists. Being able to restore a photograph is not a reason to be handed the
// unredacted original of it, and the API enforces that rather than trusting
// this component to ask nicely.
// ---------------------------------------------------------------------------

interface Props {
    postId: number;
    /** re-fetch the post so a restored image rejoins the gallery */
    onRestored: () => void | Promise<void>;
}

const daysLeft = (restorableUntil: string): number =>
    Math.max(1, Math.ceil((new Date(restorableUntil).getTime() - Date.now()) / 86_400_000));

const DeletedPostImages: React.FC<Props> = ({ postId, onRestored }) => {
    const [uploads, setUploads] = useState<DeletedUpload[]>([]);
    const [open, setOpen] = useState(false);
    const [restoringId, setRestoringId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await PostDataService.getDeletedUploads(postId);
            setUploads(r.uploads);
        } catch {
            // Silent: this is a secondary affordance, and a failure here must
            // not put an error banner across a post the user is trying to read.
            setUploads([]);
        }
    }, [postId]);

    useEffect(() => { if (postId > 0) load(); }, [postId, load]);

    const handleRestore = async (upload: DeletedUpload) => {
        setRestoringId(upload.id);
        setError(null);
        try {
            await PostDataService.restoreUpload(postId, upload.id);
            setUploads(prev => prev.filter(u => u.id !== upload.id));
            await onRestored();
        } catch (err: any) {
            const expired = isPastRestoreWindow(err);
            setError(expired
                ? 'That image passed its restore window and the file has already been permanently deleted.'
                : 'Could not restore that image. Please try again.');
            if (expired) setUploads(prev => prev.filter(u => u.id !== upload.id));
        } finally {
            setRestoringId(null);
        }
    };

    if (uploads.length === 0) return null;

    return (
        <div className="mt-2">
            <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={() => setOpen(o => !o)}
            >
                {open ? 'Hide' : 'Show'} {uploads.length} deleted image{uploads.length === 1 ? '' : 's'}
            </button>

            {open && (
                <div className="mt-2">
                    <p className="small text-muted mb-2">
                        These were deleted and will be permanently removed when their time runs out.
                    </p>
                    {error && <div className="alert alert-warning py-2 small">{error}</div>}
                    <div className="d-flex flex-wrap gap-2">
                        {uploads.map(u => {
                            const left = daysLeft(u.restorable_until);
                            return (
                                <div key={u.id} className="text-center" style={{ width: 120 }}>
                                    <img
                                        src={u.thumbnail_url}
                                        alt={u.file_name}
                                        className="img-fluid rounded"
                                        style={{ opacity: 0.55, aspectRatio: '1 / 1', objectFit: 'cover' }}
                                    />
                                    <div className={`small mt-1 ${left <= 2 ? 'text-danger fw-semibold' : 'text-muted'}`}>
                                        {left} day{left === 1 ? '' : 's'} left
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-primary mt-1 w-100"
                                        disabled={restoringId === u.id}
                                        onClick={() => handleRestore(u)}
                                    >
                                        {restoringId === u.id ? '…' : 'Restore'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeletedPostImages;
